import { Slider, Typography, Grid, Paper, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import {
    DataGrid,
    GridColDef,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarFilterButton,
    GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import { auth } from "../firebase/firebase";
import { argosApiEntryPoint } from "../GlobalConfig";

const START_YEAR = 2014;
const END_YEAR = 2025;

interface JournalSummaryResults {
    results: JournalSummary[],
    metadata: SummaryMetadata
}

interface SummaryMetadata {
    startYear: number,
    endYear: number
}

interface JournalSummary {
    journal: string,
    publisher: string,
    retractions: number,
    numberOfHighRiskArticles: number,
    retractionsPer10kArticles: number,
    highRiskPer10kArticles: number,
    actions: string
}


function valuetext(value: number) {
    return `${value}`;
}


const marks = [
    {
        value: START_YEAR,
        label: '' + START_YEAR,
    },
    {
        value: 2020,
        label: '2020',
    },
    {
        value: END_YEAR,
        label: '' + END_YEAR,
    }
];

function PublicJournalSummaryTable() {

    const [hasInitialData, setHasInitialData] = useState(false)
    const [retractionsPerJournal, setRetractionsPerJournal] = useState<any[]>()
    const [yearRange, setYearRange] = useState<number[]>([START_YEAR, END_YEAR]);
    const [shownStartYear, setShownStartYear] = useState<number>(START_YEAR);
    const [shownEndYear, setShownEndYear] = useState<number>(END_YEAR);
    const [open, setOpen] = useState<boolean>(false); // used to show error to non paid accounts
    const startYearInUse = useRef(START_YEAR)
    const endYearInUse = useRef(END_YEAR)

    const handleAdvancedFeaturesAlertClose = () => {
        setOpen(false);
    };

    const handleSliderChange = (event: Event, newValue: number | number[]) => {
        setYearRange(newValue as number[]);
    };

    const columnHeaders: any[][] = [
        [
            { type: "string", label: "Journal" },
            { type: "string", label: "Publisher" },
            { type: "number", label: "# Retractions" },
            { type: "number", label: "# High Risk Articles" },
            { type: "number", label: "Retractions per 10k articles" },
            { type: "number", label: "High Risk per 10k articles" },
            { type: "string", label: "Actions" }
        ]
    ]

    function CustomToolbar() {
        return (
            <GridToolbarContainer sx={{ marginTop: 2 }}>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarQuickFilter sx={{ paddingLeft: 5 }} />
            </GridToolbarContainer>
        );
    }

    const columns: GridColDef<(JournalSummary)>[] = [
        { field: 'journal', headerName: 'Journal', flex: 0.5 },
        { field: 'publisher', headerName: 'Publisher', flex: 0.5 },
        { field: 'totalAnalysed', headerName: '# Analysed', flex: 0.3 },
        { field: 'retractions', headerName: '# Retractions', flex: 0.3 },
        { field: 'numberOfHighRiskArticles', headerName: '# High Risk', flex: 0.3 },
        { field: 'retractionsPer10kArticles', headerName: 'Retractions per 10k', flex: 0.3 },
        { field: 'highRiskPer10kArticles', headerName: 'High Risk per 10k', flex: 0.3 }
    ];

    useEffect(() => {

        async function fetchData() {
            startYearInUse.current = yearRange[0]
            endYearInUse.current = yearRange[1]
            let authToken = await auth.currentUser?.getIdToken(true)

            fetch(argosApiEntryPoint + '/stats/public/journal-summary?startYear=' + yearRange[0] + '&endYear=' + yearRange[1]
                , {
                    method: 'GET',
                    headers: {
                        'content-type': 'application/json;charset=UTF-8',
                        'Authorization': 'Bearer ' + authToken
                    }
                })
                .then((response) => response.json())
                .then((data: JournalSummaryResults) => {
                    if (data.metadata.startYear === startYearInUse.current && data.metadata.endYear === endYearInUse.current) {
                        setHasInitialData(true)
                        setShownStartYear(data.metadata.startYear)
                        setShownEndYear(data.metadata.endYear)
                        setRetractionsPerJournal(data.results)
                    }
                })
        }
        fetchData()
    }, [yearRange]);

    /**const handleChange = (
        event: React.MouseEvent<HTMLElement>,
        newMode: string,
    ) => {
        if (newMode === "by_publication_year") {
            setMode(byPublicationYearMode)
        } else {
            setMode(byRetractionYearMode)
        }
    };**/

    function getRowId(row: JournalSummary) {
        return row.publisher + "_" + row.journal;
    }

    return <Paper elevation={5} sx={{ marginBottom: 5, padding: 2, width: '90%' }}>
        <Grid container>
            <Grid item xs={12} padding={2}>
                <Typography fontFamily='Roboto' sx={{ color: '#003366' }} variant="h5">
                    Journals Summary (journals over 10k articles analysed within timeframe)
                </Typography>
                <Typography fontFamily='Roboto' sx={{ color: '#003366' }} variant="h6">
                    {"Selected timeframe: " + shownStartYear + " - " + shownEndYear}
                </Typography>
            </Grid>
            <Grid item xs={12} md={6} padding={2}></Grid>            
            <Grid item xs={12} md={6} sx={{ paddingRight: 2, paddingTop: 3 }}>
                <Slider
                    aria-labelledby="track-inverted-range-slider"
                    getAriaValueText={valuetext}
                    defaultValue={[START_YEAR, END_YEAR]}
                    onChange={handleSliderChange}
                    min={START_YEAR}
                    valueLabelDisplay="auto"
                    max={END_YEAR}
                    marks={marks}
                />
            </Grid>
            {hasInitialData &&
                <>
                    <Dialog
                        open={open}
                        onClose={handleAdvancedFeaturesAlertClose}
                        aria-labelledby="alert-dialog-title"
                        aria-describedby="alert-dialog-description"
                    >
                        <DialogTitle id="alert-dialog-title">
                            {"Interested in seeing the affected articles?"}
                        </DialogTitle>
                        <DialogContent>
                            <DialogContentText id="alert-dialog-description">
                                Argos has the complete list of retractions and high risk articles for this journal.   Interested? Contact us at hello@scitility.com
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleAdvancedFeaturesAlertClose}>OK</Button>
                        </DialogActions>
                    </Dialog>
                    <Grid item xs={12}>
                        <DataGrid
                            rows={retractionsPerJournal}
                            columns={columns}
                            getRowId={getRowId}
                            initialState={{
                                columns: {
                                    columnVisibilityModel: {
                                        publisher: false
                                    },
                                },
                                pagination: {
                                    paginationModel: {
                                        pageSize: 10,
                                    },
                                },
                            }}
                            columnHeaderHeight={120}
                            pageSizeOptions={[10, 20, 50]}
                            slots={{
                                toolbar: CustomToolbar,
                            }}
                            sx={{ '& .MuiDataGrid-virtualScroller::-webkit-scrollbar': { display: 'none' } }}
                        />
                    </Grid>
                </>
            }
            <Grid item xs={12} marginTop={2} paddingRight={2} >
                <Typography fontFamily='Roboto' color='#003366' align="right" sx={{ fontStyle: 'italic' }}>
                    Powered by Scitility PBC
                </Typography>
            </Grid>
        </Grid>
    </Paper>
}

export default PublicJournalSummaryTable